import { useState, useEffect, useRef, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { getSignedUrl } from '@/api/storage';

export function BottomNav() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user, profile, signOut } = useAuth();
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (profile?.avatar_path) {
      getSignedUrl(profile.avatar_path).then(setAvatarUrl).catch(() => setAvatarUrl(null));
    } else {
      setAvatarUrl(null);
    }
  }, [profile?.avatar_path]);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [menuOpen]);

  const isPremium = !!(profile?.is_premium && profile?.premium_until && new Date(profile.premium_until) > new Date());
  const displayName = profile?.display_name ?? (user?.user_metadata?.full_name as string | undefined) ?? '';
  const initials = displayName
    ? displayName.split(' ').map((n: string) => n[0]).slice(0, 2).join('').toUpperCase()
    : (user?.email?.slice(0, 2).toUpperCase() ?? '?');

  const isActive = (to: string) => to === '/' ? pathname === '/' : pathname.startsWith(to);
  const profileActive = menuOpen || isActive('/perfil');

  const go = (to: string) => { setMenuOpen(false); navigate(to, { replace: true }); };
  const handleSignOut = () => {
    setMenuOpen(false);
    void signOut().then(() => navigate('/', { replace: true }));
  };

  const avatarEl = (
    <span className="relative">
      <span className="w-7 h-7 rounded-full overflow-hidden bg-brand flex items-center justify-center text-white text-xs font-bold block">
        {avatarUrl
          ? <img src={avatarUrl} alt="" className="w-full h-full object-cover" />
          : <span>{initials}</span>
        }
      </span>
      {isPremium && (
        <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-full flex items-center justify-center border border-white pointer-events-none">
          <svg viewBox="0 0 20 20" fill="currentColor" className="w-2 h-2 text-white" aria-hidden="true">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        </span>
      )}
    </span>
  );

  return (
    <>
      <nav className="fixed bottom-0 left-0 right-0 z-[1000] border-t border-white/70 bg-white/90 pb-[env(safe-area-inset-bottom)] shadow-[0_-18px_45px_rgba(15,23,42,0.10)] backdrop-blur-2xl">
        <div className="flex items-center justify-around h-16">

          <NavItem to="/" label="Início" active={isActive('/')} icon={
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" /><polyline points="9 22 9 12 15 12 15 22" />
            </svg>
          } />

          <NavItem to="/mapa" label="Mapa" active={isActive('/mapa')} icon={
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
            </svg>
          } />

          <Link
            to="/anunciar"
            replace
            className="-mt-5 flex flex-col items-center gap-1 flex-1 py-2"
            aria-label="Anunciar imóvel"
          >
            <span className="w-14 h-14 rounded-full bg-gradient-to-br from-brand-dark via-brand to-brand-light flex items-center justify-center shadow-glow ring-4 ring-white transition-transform duration-200 hover:-translate-y-0.5">
              <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round">
                <path d="M12 5v14M5 12h14" />
              </svg>
            </span>
            <span className="text-[10px] font-medium text-brand">Anunciar</span>
          </Link>

          <NavItem to="/favoritos" label="Favoritos" active={isActive('/favoritos')} icon={
            <svg className="w-5 h-5" fill={isActive('/favoritos') ? 'currentColor' : 'none'} viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
            </svg>
          } />

          {user ? (
            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              aria-haspopup="dialog"
              aria-expanded={menuOpen}
              className={['flex flex-col items-center gap-1 flex-1 py-2 transition-colors', profileActive ? 'text-brand' : 'text-muted'].join(' ')}
            >
              {avatarEl}
              <span className="text-[10px] font-medium">Perfil</span>
            </button>
          ) : (
            <Link
              to="/login"
              replace
              className={['flex flex-col items-center gap-1 flex-1 py-2 transition-colors', isActive('/perfil') ? 'text-brand' : 'text-muted'].join(' ')}
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
                <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 11a4 4 0 100-8 4 4 0 000 8z" />
              </svg>
              <span className="text-[10px] font-medium">Perfil</span>
            </Link>
          )}

        </div>
      </nav>

      {user && (
        <ProfileSheet
          open={menuOpen}
          onClose={() => setMenuOpen(false)}
          avatarUrl={avatarUrl}
          initials={initials}
          displayName={displayName}
          email={user.email ?? ''}
          isPremium={isPremium}
          isAdmin={!!profile?.is_admin}
          onNavigate={go}
          onSignOut={handleSignOut}
        />
      )}
    </>
  );
}

function NavItem({ to, label, icon, active }: { to: string; label: string; icon: ReactNode; active: boolean }) {
  return (
    <Link
      to={to}
      replace
      className={['flex flex-col items-center gap-1 flex-1 py-2 transition-colors', active ? 'text-brand' : 'text-muted'].join(' ')}
    >
      {icon}
      <span className="text-[10px] font-medium">{label}</span>
    </Link>
  );
}

interface ProfileSheetProps {
  open: boolean;
  onClose: () => void;
  avatarUrl: string | null;
  initials: string;
  displayName: string;
  email: string;
  isPremium: boolean;
  isAdmin: boolean;
  onNavigate: (to: string) => void;
  onSignOut: () => void;
}

function ProfileSheet({ open, onClose, avatarUrl, initials, displayName, email, isPremium, isAdmin, onNavigate, onSignOut }: ProfileSheetProps) {
  const [dragY, setDragY] = useState<number | null>(null);
  const startY = useRef(0);
  const dragging = useRef(false);

  useEffect(() => { if (!open) setDragY(null); }, [open]);

  const onTouchStart = (e: React.TouchEvent) => {
    startY.current = e.touches[0].clientY;
    dragging.current = true;
    setDragY(0);
  };
  const onTouchMove = (e: React.TouchEvent) => {
    if (!dragging.current) return;
    const dy = e.touches[0].clientY - startY.current;
    if (dy > 0) setDragY(dy);
    else setDragY(0);
  };
  const onTouchEnd = () => {
    dragging.current = false;
    if (dragY !== null && dragY > 80) {
      onClose();
    } else {
      setDragY(null);
    }
  };

  const panelStyle = dragY !== null && dragY > 0
    ? { transform: `translateY(${dragY}px)`, transition: 'none' as const }
    : undefined;

  return (
    <div
      className={[
        'fixed inset-0 z-[1100] overflow-hidden',
        open ? '' : 'pointer-events-none',
      ].join(' ')}
      role="dialog"
      aria-modal="true"
      aria-hidden={!open}
      aria-label="Menu do perfil"
    >
      <div
        onClick={onClose}
        aria-hidden="true"
        className={[
          'absolute inset-0 bg-black/40 transition-opacity duration-300',
          open ? 'opacity-100' : 'opacity-0',
        ].join(' ')}
      />
      <div
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
        style={panelStyle}
        className={[
          'absolute left-0 right-0 bottom-0 bg-white rounded-t-2xl shadow-2xl',
          'pb-[env(safe-area-inset-bottom)] transition-transform duration-300 ease-out',
          'will-change-transform touch-pan-y',
          open ? 'translate-y-0' : 'translate-y-full',
        ].join(' ')}
      >
        <div className="flex justify-center pt-2 pb-1">
          <span className="w-10 h-1.5 rounded-full bg-line" />
        </div>

        <button
          onClick={() => onNavigate('/perfil')}
          className="flex w-full items-center gap-3 border-b border-line px-5 py-4 text-left transition-colors hover:bg-white/70 focus-visible:bg-faint focus-visible:outline-none"
        >
          <div className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-full bg-brand text-base font-bold text-white">
            {avatarUrl
              ? <img src={avatarUrl} alt="" className="h-full w-full object-cover" aria-hidden="true" />
              : <span>{initials}</span>}
          </div>
          <div className="min-w-0">
            <p className="truncate text-base font-semibold text-ink">{displayName || 'Minha conta'}</p>
            {isPremium ? (
              <span className="mt-0.5 inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-yellow-400 to-amber-500 px-1.5 py-0.5 text-xs font-semibold text-white">
                <svg viewBox="0 0 20 20" fill="currentColor" className="h-2.5 w-2.5 shrink-0" aria-hidden="true">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                Premium
              </span>
            ) : (
              <p className="truncate text-sm text-muted">{email}</p>
            )}
          </div>
        </button>

        <div className="py-1">
          <SheetItem onClick={() => onNavigate('/meus-anuncios')} icon={<ListIcon />} label="Meus anúncios" />
          {!isPremium && (
            <SheetItem onClick={() => onNavigate('/premium')} icon={<StarIcon />} label="Conheça o Premium" highlight />
          )}
          {isPremium && (
            <SheetItem onClick={() => onNavigate('/painel')} icon={<ChartIcon />} label="Painel Premium" />
          )}
          {isAdmin && (
            <SheetItem onClick={() => onNavigate('/admin')} icon={<ShieldIcon />} label="Painel Admin" />
          )}
          <SheetItem onClick={() => onNavigate('/favoritos')} icon={<HeartIcon />} label="Favoritos" />
        </div>
        <div className="border-t border-line py-1">
          <SheetItem onClick={onSignOut} icon={<LogoutIcon />} label="Sair" muted />
        </div>
      </div>
    </div>
  );
}

function SheetItem({ onClick, icon, label, highlight, muted }: { onClick: () => void; icon: ReactNode; label: string; highlight?: boolean; muted?: boolean }) {
  return (
    <button
      onClick={onClick}
      className={[
        'flex w-full items-center gap-3 px-5 py-3.5 text-left text-[15px]',
        'transition-colors focus-visible:bg-faint focus-visible:outline-none',
        highlight ? 'font-semibold text-brand hover:bg-brand/5'
          : muted ? 'text-muted hover:bg-faint'
          : 'text-ink hover:bg-faint',
      ].join(' ')}
    >
      <span className={`h-5 w-5 shrink-0 ${highlight ? 'text-brand' : 'text-muted'}`}>{icon}</span>
      {label}
    </button>
  );
}

function ChartIcon() {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><path d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>;
}
function ListIcon() {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>;
}
function HeartIcon() {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" /></svg>;
}
function LogoutIcon() {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><path d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>;
}
function ShieldIcon() {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>;
}
function StarIcon() {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.52 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.364 1.118l1.52 4.674c.3.921-.755 1.688-1.54 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.784.57-1.838-.197-1.539-1.118l1.52-4.674a1 1 0 00-.364-1.118L2.49 10.1c-.783-.57-.38-1.81.588-1.81h4.915a1 1 0 00.951-.69l1.52-4.674z" /></svg>;
}
